// src/components/Login/TermsCheckbox.jsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, FileText, ExternalLink, Info, AlertCircle } from 'lucide-react';

/**
 * Componente de checkbox de termos de uso profissional
 * Nível: Google/Airbnb/Spotify
 * 
 * Características:
 * - Checkbox animado com feedback visual
 * - Links para termos e privacidade
 * - Modal de confirmação (opcional)
 * - Validação em tempo real
 * - Acessibilidade completa
 * - Design responsivo
 * - Preparado para GDPR/LGPD
 */

// ============================================
// 🎨 VARIAÇÕES DE TEMA (cores)
// ============================================
const THEME_VARIANTS = {
  light: {
    checkbox: {
      unchecked: 'border-slate-300 bg-white',
      checked: 'border-blue-600 bg-blue-600',
      hover: 'hover:border-blue-400 hover:bg-blue-50',
      focus: 'focus:ring-blue-200'
    },
    text: 'text-slate-600',
    link: 'text-blue-600 hover:text-blue-700',
    modal: {
      bg: 'bg-white',
      border: 'border-slate-200',
      text: 'text-slate-800',
      description: 'text-slate-600'
    }
  },
  dark: {
    checkbox: {
      unchecked: 'border-slate-600 bg-slate-800',
      checked: 'border-blue-500 bg-blue-500',
      hover: 'hover:border-blue-400 hover:bg-slate-700',
      focus: 'focus:ring-blue-800'
    },
    text: 'text-slate-300',
    link: 'text-blue-400 hover:text-blue-300',
    modal: {
      bg: 'bg-slate-800',
      border: 'border-slate-700',
      text: 'text-slate-100',
      description: 'text-slate-400'
    }
  }
};

/**
 * Componente principal de termos de uso
 * @param {Object} props
 * @param {boolean} props.checked - Estado do checkbox
 * @param {function} props.onChange - Callback quando muda
 * @param {boolean} props.required - Se é obrigatório (default: true)
 * @param {string} props.theme - 'light' | 'dark' (default: 'light')
 * @param {boolean} props.showModal - Se mostra modal de confirmação (default: false)
 * @param {string} props.size - 'sm' | 'md' | 'lg' (default: 'md')
 * @param {Object} props.links - Links personalizados
 * @param {string} props.error - Mensagem de erro
 * @param {boolean} props.disabled - Desabilitado
 */
const TermsCheckbox = ({
  checked = false,
  onChange,
  required = true,
  theme = 'light',
  showModal = false,
  size = 'md',
  links = {
    terms: '/termos',
    privacy: '/privacidade'
  },
  error,
  disabled = false
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [modalStep, setModalStep] = useState(null); // 'terms' | 'privacy'

  const colors = THEME_VARIANTS[theme];

  // ============================================
  // 📏 CONFIGURAÇÃO DE TAMANHOS
  // ============================================
  const sizeClasses = {
    sm: {
      checkbox: 'w-4 h-4',
      icon: 'w-3 h-3',
      text: 'text-xs',
      gap: 'gap-2',
      modal: 'max-w-md'
    },
    md: {
      checkbox: 'w-5 h-5',
      icon: 'w-4 h-4',
      text: 'text-sm',
      gap: 'gap-3',
      modal: 'max-w-lg'
    },
    lg: {
      checkbox: 'w-6 h-6',
      icon: 'w-5 h-5',
      text: 'text-base',
      gap: 'gap-4',
      modal: 'max-w-xl'
    }
  };

  // ============================================
  // 🎯 MANIPULADORES DE EVENTOS
  // ============================================
  const handleCheckboxChange = (e) => {
    if (disabled) return;
    
    const newChecked = e.target.checked;
    
    // Se showModal estiver ativado e estiver marcando
    if (showModal && newChecked && !checked) {
      // Abre modal de confirmação
      setModalStep('terms');
      setShowTermsModal(true);
      return;
    }
    
    // Caso contrário, muda diretamente
    onChange?.(newChecked);
  };

  const handleModalConfirm = () => {
    if (modalStep === 'terms') {
      // Após confirmar termos, mostra privacidade
      setModalStep('privacy');
    } else {
      // Após confirmar tudo, marca checkbox
      setShowTermsModal(false);
      setModalStep(null);
      onChange?.(true);
    }
  };

  const handleModalClose = () => {
    setShowTermsModal(false);
    setModalStep(null);
  };

  const handleLinkClick = (e, type) => {
    e.preventDefault();
    
    // Dispara evento personalizado para tracking
    window.dispatchEvent(new CustomEvent('terms:link:click', {
      detail: { type, timestamp: new Date().toISOString() }
    }));
    
    // Se showModal, abre modal com os termos
    if (showModal) {
      setModalStep(type);
      setShowTermsModal(true);
    } else {
      // Senão, navega normalmente
      window.open(links[type], '_blank');
    }
  };

  // ============================================
  // 🎨 ANIMAÇÕES
  // ============================================
  const checkboxVariants = {
    unchecked: {
      scale: 1,
      backgroundColor: colors.checkbox.unchecked,
      borderColor: colors.checkbox.unchecked
    },
    checked: {
      scale: [1, 1.2, 1],
      backgroundColor: colors.checkbox.checked,
      borderColor: colors.checkbox.checked,
      transition: {
        duration: 0.2,
        times: [0, 0.5, 1]
      }
    },
    hover: {
      scale: 1.05,
      borderColor: colors.checkbox.hover.split(' ')[1]
    },
    tap: {
      scale: 0.95
    }
  };

  const modalVariants = {
    hidden: {
      opacity: 0,
      scale: 0.9,
      y: 20
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: 'spring',
        damping: 25,
        stiffness: 300
      }
    },
    exit: {
      opacity: 0,
      scale: 0.9,
      y: 20,
      transition: {
        duration: 0.2
      }
    }
  };

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 }
  };

  return (
    <>
      {/* ======================================== */}
      {/* CHECKBOX PRINCIPAL */}
      {/* ======================================== */}
      <div className={`flex items-start ${sizeClasses[size].gap}`}>
        {/* Checkbox customizado */}
        <div className="relative flex items-center justify-center">
          <motion.input
            type="checkbox"
            checked={checked}
            onChange={handleCheckboxChange}
            onHoverStart={() => setIsHovered(true)}
            onHoverEnd={() => setIsHovered(false)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            disabled={disabled}
            className="
              absolute 
              w-full 
              h-full 
              opacity-0 
              cursor-pointer
              disabled:cursor-not-allowed
            "
          />
          
          {/* Checkbox visual */}
          <motion.div
            variants={checkboxVariants}
            initial="unchecked"
            animate={checked ? 'checked' : 'unchecked'}
            whileHover={!disabled && !checked ? 'hover' : undefined}
            whileTap={!disabled && !checked ? 'tap' : undefined}
            className={`
              ${sizeClasses[size].checkbox}
              rounded
              border-2
              flex
              items-center
              justify-center
              transition-shadow
              ${colors.checkbox.focus}
              ${isFocused ? 'ring-4' : ''}
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
            style={{
              boxShadow: isFocused ? `0 0 0 4px ${colors.checkbox.focus}` : 'none'
            }}
          >
            <AnimatePresence>
              {checked && (
                <motion.div
                  initial={{ scale: 0, rotate: -90 }}
                  animate={{ scale: 1, rotate: 0 }}
                  exit={{ scale: 0, rotate: 90 }}
                  transition={{ duration: 0.15 }}
                >
                  <Check 
                    className={`${sizeClasses[size].icon} text-white`}
                    strokeWidth={3}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>

        {/* Texto dos termos */}
        <label className={`flex-1 ${sizeClasses[size].text} ${colors.text}`}>
          <span className="flex items-center gap-1 flex-wrap">
            Eu li e concordo com os{' '}
            <button
              type="button"
              onClick={(e) => handleLinkClick(e, 'terms')}
              className={`
                inline-flex items-center gap-1
                font-medium
                ${colors.link}
                hover:underline
                focus:outline-none
                focus:ring-2
                focus:ring-offset-2
                ${colors.checkbox.focus}
                rounded
                px-1
                transition-all
              `}
              disabled={disabled}
            >
              <FileText className="w-3.5 h-3.5" />
              Termos de Uso
              <ExternalLink className="w-3 h-3 opacity-50" />
            </button>
            {' '}e{' '}
            <button
              type="button"
              onClick={(e) => handleLinkClick(e, 'privacy')}
              className={`
                inline-flex items-center gap-1
                font-medium
                ${colors.link}
                hover:underline
                focus:outline-none
                focus:ring-2
                focus:ring-offset-2
                ${colors.checkbox.focus}
                rounded
                px-1
                transition-all
              `}
              disabled={disabled}
            >
              <Info className="w-3.5 h-3.5" />
              Política de Privacidade
              <ExternalLink className="w-3 h-3 opacity-50" />
            </button>
            {required && <span className="text-red-500 ml-1">*</span>}
          </span>
        </label>
      </div>

      {/* ======================================== */}
      {/* MENSAGEM DE ERRO */}
      {/* ======================================== */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-1.5 mt-1 text-xs text-red-500"
          >
            <AlertCircle className="w-3.5 h-3.5" />
            <span>{error}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ======================================== */}
      {/* MODAL DE CONFIRMAÇÃO (GDPR/LGPD style) */}
      {/* ======================================== */}
      <AnimatePresence>
        {showTermsModal && (
          <>
            {/* Overlay */}
            <motion.div
              variants={overlayVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
              onClick={handleModalClose}
            />

            {/* Modal */}
            <motion.div
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className={`
                fixed 
                left-1/2 
                top-1/2 
                -translate-x-1/2 
                -translate-y-1/2
                z-50
                w-[90vw]
                ${sizeClasses[size].modal}
                ${colors.modal.bg}
                border
                ${colors.modal.border}
                rounded-2xl
                shadow-2xl
                overflow-hidden
              `}
            >
              {/* Header */}
              <div className="p-6 border-b border-slate-200">
                <h3 className={`text-xl font-semibold ${colors.modal.text}`}>
                  {modalStep === 'terms' ? 'Termos de Uso' : 'Política de Privacidade'}
                </h3>
              </div>

              {/* Content */}
              <div className="p-6 max-h-[60vh] overflow-y-auto">
                <div className={`space-y-4 ${colors.modal.description}`}>
                  {modalStep === 'terms' ? (
                    <>
                      <p>
                        Ao utilizar nossa plataforma, você concorda com os seguintes termos:
                      </p>
                      <ul className="list-disc pl-5 space-y-2">
                        <li>Você é responsável por manter a confidencialidade da sua conta</li>
                        <li>Não compartilhe suas credenciais com terceiros</li>
                        <li>Respeite os direitos de propriedade intelectual</li>
                        <li>Use a plataforma apenas para fins legais</li>
                      </ul>
                    </>
                  ) : (
                    <>
                      <p>
                        Valorizamos sua privacidade e protegemos seus dados:
                      </p>
                      <ul className="list-disc pl-5 space-y-2">
                        <li>Seus dados são armazenados localmente (offline-first)</li>
                        <li>Não compartilhamos informações com terceiros</li>
                        <li>Você pode exportar seus dados a qualquer momento</li>
                        <li>Cookies apenas para funcionalidade</li>
                      </ul>
                    </>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-slate-200 bg-slate-50">
                <div className="flex justify-end gap-3">
                  <button
                    onClick={handleModalClose}
                    className="
                      px-4 
                      py-2 
                      text-sm 
                      font-medium 
                      text-slate-700
                      hover:text-slate-900
                      hover:bg-slate-100
                      rounded-lg
                      transition-colors
                    "
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleModalConfirm}
                    className="
                      px-4 
                      py-2 
                      text-sm 
                      font-medium 
                      text-white
                      bg-blue-600
                      hover:bg-blue-700
                      rounded-lg
                      transition-colors
                      focus:outline-none
                      focus:ring-2
                      focus:ring-blue-500
                      focus:ring-offset-2
                    "
                  >
                    {modalStep === 'terms' ? 'Continuar' : 'Confirmar'}
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

// ✅ Otimização: memo para evitar re-renders desnecessários
export default React.memo(TermsCheckbox);